import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  Chip,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  FormControlLabel,
  Switch
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon
} from "@mui/icons-material";
import Header from "../components/Header";
import ObjectCard from "../components/Common/ObjectCard";
import axios from "axios";
import { useApi } from '../hooks/useApi'
import { v4 as uuidv4 } from 'uuid';

export default function PromptManager() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editPrompt, setEditPrompt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLang, setFilterLang] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPromptMenu, setSelectedPromptMenu] = useState(null);
  const { callApi, fetching, error } = useApi();
  const [input, setInput] = useState("");

  // Valores por defecto para un nuevo prompt
  const getDefaultPrompt = () => ({
    document_type: "",
    lang: "es",
    max_tokens: 1024,
    model_default: "anthropic.claude-3-haiku-20240307-v1:0",
    model_fallback: "",
    temperature: 0.2,
    top_k: 250,
    top_p: 0.999,
    description: "",
    prompt_template: "",
    stop_sequences: ["Human"],
    enabled: true
  });

  // Iconos para diferentes tipos de documentos
  const getIconForDocumentType = (type) => {
    const icons = {
      "unclassified": "📄",
      "invoice": "🧾",
      "receipt": "🎫",
      "quoteap": "📋",
      "insurance policy": "📑",
      "email": "✉️",
      "report": "📊",
      "chat": "💬",
      "document": "📄",
      "template": "📋",
      "default": "🎯"
    };
    return icons[type?.toLowerCase()] || icons.default;
  };

  // Obtener tags del prompt - versión simplificada para ahorrar espacio
  const getPromptTags = (prompt) => {
    const tags = [];
    if (prompt.source_template_id) tags.push("Desde catálogo");
    if (prompt.lang) tags.push(prompt.lang.toUpperCase());
    if (prompt.model_fallback) tags.push("FB");
    if (prompt.temperature > 0.7) tags.push("Creativo");
    else if (prompt.temperature < 0.3) tags.push("Preciso");
    return tags;
  };

  const PROMPTS_TEMPLATES_QUERY = `query ListPromptTemplates {
    listPromptTemplates(orderBy: "document_type") {
        totalCount
        items {
            id
            document_type
            prompt_template
            model_default
            model_fallback
            lang
            temperature
            max_tokens
            top_p
            top_k
            stop_sequences
            createdAt
            updatedAt
            enabled
            description
            tenant_id
            source_template_id
            seeded_at
        }
    }
}`

const PROMPTS_TEMPLATES_MUTATION = ` 
mutation SavePromptTemplate($input: PromptTemplateInput!) {
    savePromptTemplate(input: $input) { 
        id
        document_type
        prompt_template
        model_default
        model_fallback
        lang
        temperature
        max_tokens
        top_p
        top_k
        stop_sequences
        createdAt
        updatedAt
        enabled
        description
    }
}`
  // Traer datos del backend
  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const data = await callApi(PROMPTS_TEMPLATES_QUERY);
      setPrompts(
        data.listPromptTemplates.items.map(item => ({
          ...item,
          enabled: item.enabled === true || item.enabled === "true"
        }))
      );
    } catch (err) {
      console.error("Error fetching prompts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  // Filtrar prompts basado en búsqueda y filtros
  const filteredPrompts = prompts.filter(prompt => {
    const matchSearch = 
      prompt.document_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.model_default?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchLang = filterLang === "all" || prompt.lang === filterLang;
    return matchSearch && matchLang;
  });

  // Handler para abrir el diálogo de edición con los datos del prompt
  const handleOpenEdit = (promptId) => {
    const promptToEdit = prompts.find(p => p.id === promptId);
    if (promptToEdit) {
      setEditPrompt({
        ...promptToEdit,
        stop_sequences: promptToEdit.stop_sequences || ["Human"],
        description: promptToEdit.description || "",
        prompt_template: promptToEdit.prompt_template || ""
      });
      setOpenDialog(true);
    }
  };

  // Handler para crear nuevo prompt
  const handleCreateNew = () => {
    setEditPrompt(getDefaultPrompt());
    setOpenDialog(true);
  };

  // Handler para editar desde el menú
  const handleEdit = (prompt) => {
    if (prompt && prompt.id) {
      handleOpenEdit(prompt.id);
    }
    handleCloseMenu();
  };

  // Handler para duplicar prompt
  const handleDuplicate = (prompt) => {
    if (prompt) {
      const duplicated = {
        ...prompt,
        id: undefined,
        document_type: `${prompt.document_type} (copia)`,
        description: prompt.description ? `${prompt.description} - Copia` : "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setEditPrompt(duplicated);
      setOpenDialog(true);
    }
    handleCloseMenu();
  };

  // Handler para eliminar prompt
  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro de que desea eliminar este prompt?")) {
      try {
        await axios.delete(`http://localhost:4000/prompts/${id}`);
        fetchPrompts();
      } catch (err) {
        console.error("Error deleting prompt:", err);
      }
    }
    handleCloseMenu();
  };

  // Handler para toggle de activación
  const handleToggleActive = async (promptId, newState) => {
    const prompt = prompts.find(p => p.id === promptId);
    if (prompt) {
      try {
        const updatedPromptInput = { 
          id: prompt.id,
          enabled: newState.toString(),
          updatedAt: new Date().toISOString()
        };
        await callApi(PROMPTS_TEMPLATES_MUTATION, { input: updatedPromptInput });
        fetchPrompts();
      } catch (err) {
        console.error("Error toggling prompt:", err);
      }
    }
  };

  // Handler para guardar cambios
  const handleSave = async () => {
    try {
      const promptData = {
        id: editPrompt.id,
        stop_sequences: editPrompt.stop_sequences || ["Human"],
        max_tokens: parseInt(editPrompt.max_tokens) || 1024,
        temperature: parseFloat(editPrompt.temperature) || 0.2,
        top_k: parseInt(editPrompt.top_k) || 250,
        top_p: parseFloat(editPrompt.top_p) || 0.999,
        enabled: editPrompt.enabled.toString(),
        model_default: editPrompt.model_default,
        model_fallback: editPrompt.model_fallback,
        lang: editPrompt.lang,
        description: editPrompt.description,
        document_type: editPrompt.document_type,
        prompt_template: editPrompt.prompt_template

      };
      if (editPrompt.id) {
        const updatedPrompt = {
          ...promptData,
          updatedAt: new Date().toISOString()
        };
        await callApi(PROMPTS_TEMPLATES_MUTATION, { input: updatedPrompt });
      } else {
        const newPrompt = {
          ...promptData,
          id: uuidv4(),
          enabled: 'true',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        await callApi(PROMPTS_TEMPLATES_MUTATION, { input: newPrompt });
      }
      
      setOpenDialog(false);
      setEditPrompt(null);
      fetchPrompts();
    } catch (err) {
      console.error("Error saving prompt:", err);
    }
  };

  // Handler para cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditPrompt(null);
  };

  // Menu handlers
  const handleOpenMenu = (event, prompt) => {
    setAnchorEl(event.currentTarget);
    setSelectedPromptMenu(prompt);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPromptMenu(null);
  };

  // Obtener idiomas únicos para el filtro
  const uniqueLangs = [...new Set(prompts.map(p => p.lang).filter(Boolean))];

  // Obtener lista de modelos disponibles
  const availableModels = [
    { value: "anthropic.claude-3-haiku-20240307-v1:0", label: "Claude 3 Haiku" },
    { value: "anthropic.claude-3-sonnet-20240229-v1:0", label: "Claude 3 Sonnet" },
    { value: "anthropic.claude-3-opus-20240229-v1:0", label: "Claude 3 Opus" },
    { value: "gpt-4", label: "GPT-4" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
    { value: "gemini-pro", label: "Gemini Pro" }
  ];

  return (
    <div>
      <Header />

      <Container 
        maxWidth="lg"
        sx={{ 
          mt: 4, 
          mb: 4,
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* Header Section */}
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          mb={3}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Prompts Templates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestiona y configura tus plantillas de prompts
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              alignSelf: { xs: 'stretch', sm: 'auto' }
            }}
          >
            Nuevo Prompt
          </Button>
        </Stack>

        {/* Filters and Search Bar */}
        <Stack 
          direction="column" 
          spacing={2} 
          mb={3}
        >
          <TextField
            placeholder="Buscar prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: { sm: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1 
          }}>
            <Chip
              label="Todos"
              onClick={() => setFilterLang("all")}
              color={filterLang === "all" ? "primary" : "default"}
              variant={filterLang === "all" ? "filled" : "outlined"}
              size="small"
            />
            {uniqueLangs.map(lang => (
              <Chip
                key={lang}
                label={lang.toUpperCase()}
                onClick={() => setFilterLang(lang)}
                color={filterLang === lang ? "primary" : "default"}
                variant={filterLang === lang ? "filled" : "outlined"}
                size="small"
              />
            ))}
          </Box>
        </Stack>

        {/* Stats */}
        <Stack 
          direction="row" 
          spacing={1} 
          mb={3}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            label={`${filteredPrompts.length} prompts`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${filteredPrompts.filter(p => p.enabled).length} activos`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Stack>

        {/* Cards Grid - MÁXIMO 2 COLUMNAS */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',           // 1 columna en móvil
            sm: 'repeat(2, 1fr)' // 2 columnas en tablet y desktop
          },
          gap: 2,
          width: '100%'
        }}>
          {loading ? (
            // Reducido a 4 skeletons para coincidir con el layout de 2 columnas
            [...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={180}
                sx={{ borderRadius: 3 }}
              />
            ))
          ) : filteredPrompts.length > 0 ? (
            filteredPrompts.map((prompt) => (
              <Box key={prompt.id} position="relative">
                <ObjectCard
                  id={prompt.id}
                  icon={getIconForDocumentType(prompt.document_type)}
                  title={prompt.document_type || "Sin título"}
                  description={prompt.description}
                  lastUpdated={prompt.updatedAt}
                  isEnabled={prompt.enabled}
                  tags={getPromptTags(prompt)}
                  onClick={handleOpenEdit}
                  onToggle={handleToggleActive}
                  variant={prompt.enabled === false ? "default" : "info"}
                />

                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { boxShadow: 2 }
                  }}
                  onClick={(e) => handleOpenMenu(e, prompt)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))
          ) : (
            <Box sx={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No se encontraron prompts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Intenta con otros términos de búsqueda" : "Crea tu primer prompt"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => handleEdit(selectedPromptMenu)}>
            Editar
          </MenuItem>
          <MenuItem onClick={() => handleDuplicate(selectedPromptMenu)}>
            Duplicar
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedPromptMenu?.id)}
            sx={{ color: 'error.main' }}
          >
            Eliminar
          </MenuItem>
        </Menu>

        {/* Edit/Create Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editPrompt?.id ? `Editar: ${editPrompt.document_type}` : "Nuevo Prompt"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={2}>
              {/* Primera fila: Tipo de documento e idioma */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Tipo de Documento"
                  value={editPrompt?.document_type || ""}
                  onChange={(e) => setEditPrompt({ ...editPrompt, document_type: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label="Idioma"
                  value={editPrompt?.lang || "es"}
                  onChange={(e) => setEditPrompt({ ...editPrompt, lang: e.target.value })}
                  fullWidth
                  select
                >
                  <MenuItem value="es">Español</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="pt">Português</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                </TextField>
              </Stack>

              {/* Segunda fila: Tokens y temperatura */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Max Tokens"
                  type="number"
                  value={editPrompt?.max_tokens || 1024}
                  onChange={(e) => setEditPrompt({ ...editPrompt, max_tokens: parseInt(e.target.value) })}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 1, max: 4000 }
                  }}
                />
                <TextField
                  label="Temperatura"
                  type="number"
                  value={editPrompt?.temperature || 0.2}
                  onChange={(e) => setEditPrompt({ ...editPrompt, temperature: parseFloat(e.target.value) })}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, max: 2, step: 0.1 }
                  }}
                  helperText="0 = Determinístico, 2 = Muy creativo"
                />
              </Stack>

              {/* Tercera fila: Top K y Top P */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Top K"
                  type="number"
                  value={editPrompt?.top_k || 250}
                  onChange={(e) => setEditPrompt({ ...editPrompt, top_k: parseInt(e.target.value) })}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 1, max: 500 }
                  }}
                  helperText="Número de tokens a considerar"
                />
                <TextField
                  label="Top P"
                  type="number"
                  value={editPrompt?.top_p || 0.999}
                  onChange={(e) => setEditPrompt({ ...editPrompt, top_p: parseFloat(e.target.value) })}
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, max: 1, step: 0.001 }
                  }}
                  helperText="Probabilidad acumulativa"
                />
              </Stack>

              {/* Modelos */}
              <TextField
                label="Modelo Principal"
                value={editPrompt?.model_default || ""}
                onChange={(e) => setEditPrompt({ ...editPrompt, model_default: e.target.value })}
                fullWidth
                select
              >
                {availableModels.map(model => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Modelo de Respaldo (Opcional)"
                value={editPrompt?.model_fallback || ""}
                onChange={(e) => setEditPrompt({ ...editPrompt, model_fallback: e.target.value })}
                fullWidth
                select
              >
                <MenuItem value="">Ninguno</MenuItem>
                {availableModels.map(model => (
                  <MenuItem key={model.value} value={model.value}>
                    {model.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Descripción */}
              <TextField
                label="Descripción"
                value={editPrompt?.description || ""}
                onChange={(e) => setEditPrompt({ ...editPrompt, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                inputProps={{ maxLength: 160 }}
                helperText={`${editPrompt?.description?.length || 0}/160 caracteres`}
              />

              {/* Template del Prompt */}
              <TextField
                label="Template del Prompt"
                value={editPrompt?.prompt_template || ""}
                onChange={(e) => setEditPrompt({ ...editPrompt, prompt_template: e.target.value })}
                fullWidth
                multiline
                rows={6}
                placeholder="Escribe aquí el template del prompt..."
                helperText="Este es el template que se usará para generar el prompt final"
              />

              {/* Stop Sequences */}
              <TextField
                label="Stop Sequences (separadas por coma)"
                value={editPrompt?.stop_sequences?.join(", ") || "Human"}
                onChange={(e) => setEditPrompt({ 
                  ...editPrompt, 
                  stop_sequences: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                })}
                fullWidth
                helperText="Palabras que detendrán la generación"
              />

              {/* Estado activo/inactivo */}
              <FormControlLabel
                control={
                  <Switch
                    checked={editPrompt?.enabled !== false}
                    onChange={(e) => setEditPrompt({ ...editPrompt, enabled: e.target.checked })}
                  />
                }
                label="Prompt activo"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!editPrompt?.document_type}
            >
              {editPrompt?.id ? "Guardar Cambios" : "Crear Prompt"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}